import {
  type ClipboardEventHandler,
  type KeyboardEvent,
  type ReactNode,
  useId
} from "react"

import type { SX } from "../../theme"
import { BaseInput } from "../BaseInput"
import { Icon, type IconName } from "../Icon"
import { FormField } from "./FormField"

export interface FormInputProps {
  type?: "text" | "email" | "password" | "number"
  size?: "small" | "medium" | "large"
  disabled?: boolean
  label?: string
  placeholder?: string
  value: string
  error?: boolean
  icon?: IconName
  autoComplete?: AutoCompleteValue
  sx?: SX
  onKeyDown?: (event: KeyboardEvent) => void
  onValueChange?: (value: string) => void
  onPaste?: ClipboardEventHandler<HTMLInputElement>
  onBlur?: () => void
  prefix?: ReactNode
  prefixTooltip?: string
  suffix?: ReactNode
}

export function FormInput(props: FormInputProps) {
  const {
    type = "text",
    label,
    disabled = false,
    error = false,
    onValueChange,
    icon,
    sx,
    value,
    size,
    prefix,
    prefixTooltip,
    suffix,
    autoComplete,
    placeholder,
    onBlur,
    onKeyDown,
    onPaste
  } = props

  const id = useId()

  return (
    <FormField id={id} label={label} disabled={disabled} sx={sx} size={size}>
      <BaseInput
        startAdornment={
          icon && <Icon name={icon} size="medium" color="grey11" />
        }
        prefix={prefix}
        prefixTooltip={prefixTooltip}
        suffix={suffix}
        id={id}
        disabled={disabled}
        value={value}
        autoComplete={autoComplete}
        placeholder={placeholder}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        padding={{
          adornment: {
            inline: 8
          },
          input: {
            left: icon ? 0 : 12,
            right: 12
          }
        }}
        variant={error ? "error" : "normal"}
        type={type}
        onValueChange={(value) => onValueChange?.(value)}
        sx={{
          ...sx,
          alignSelf: "stretch"
        }}
        inputMode={type === "number" ? "decimal" : "text"}
      />
    </FormField>
  )
}

type AutoCompleteValue = "username" | "current-password"
